import type { EmptyStateCta } from '../components/common/EmptyStateWithRedirect';

/**
 * Empty-state preset shape (without icon — icon is a React element so
 * the caller picks it; presets are pure data the React component then
 * dresses up). Keeps this file free of JSX so it tests as a pure
 * function and stays decoupled from the icon library.
 */
export interface EmptyStatePreset {
  title: string;
  subtitle?: string;
  cta?: EmptyStateCta;
  secondaryCta?: EmptyStateCta;
}

// ── Schedule (Classic / Pro / Positions views' day-empty state) ────────

export interface ScheduleEmptyContext {
  /** True when the user is viewing today's date. */
  isToday: boolean;
  /** True when the user is viewing a past date (mutually exclusive
   *  with isToday in practice; both false = future date, which can't
   *  have activity anyway). */
  isPast: boolean;
}

/**
 * Picks the right empty-state copy + CTA for the Schedule views when
 * the day has zero strategies / positions.
 *
 *   - Today: prompt to create a strategy (most actionable next step).
 *   - Past:  informational — point to today (user is browsing history,
 *            no fix-it action available).
 *   - Future / unknown: minimal.
 */
export function pickScheduleEmpty(ctx: ScheduleEmptyContext): EmptyStatePreset {
  if (ctx.isToday) {
    return {
      title: 'No activity scheduled today',
      subtitle: 'Create a strategy in a basket to start scheduling executions.',
      cta: { label: 'Manage baskets', to: '/baskets' },
    };
  }
  if (ctx.isPast) {
    return {
      title: 'No activity on this day',
      subtitle: 'You weren\'t trading on this date. Switch to today to see live positions.',
    };
  }
  return {
    title: 'No activity on this day',
  };
}

// ── Dashboard cards ────────────────────────────────────────────────────

/**
 * "No open positions" CTA shown inside Dashboard's OpenPositionsCard.
 * Always points to the Schedule's Positions view since that's the
 * authoritative position surface (the standalone /positions page was
 * retired in Phase B3).
 */
export function pickOpenPositionsEmpty(): EmptyStatePreset {
  return {
    title: 'No open positions',
    subtitle: 'Scheduled strategies will appear here once they fire.',
    cta: { label: 'View schedule', to: '/schedule?view=positions' },
  };
}

/**
 * "No closed trades today" CTA for Dashboard's RecentTradesCard.
 * Same destination as OpenPositionsCard since the Schedule's Positions
 * view shows both live and closed legs depending on scope.
 */
export function pickRecentTradesEmpty(): EmptyStatePreset {
  return {
    title: 'No closed trades today',
    subtitle: 'Trades close throughout the day as strategies hit their exit conditions.',
    cta: { label: 'View schedule', to: '/schedule?view=positions' },
  };
}

// ── Page-level empty states ────────────────────────────────────────────

export function pickAllocationsEmpty(): EmptyStatePreset {
  return {
    title: 'No allocations yet',
    subtitle: 'Allocate baskets to broker accounts to enable trade execution.',
    cta: { label: 'Manage baskets', to: '/baskets' },
  };
}

export function pickUserSubscriptionsEmpty(): EmptyStatePreset {
  return {
    title: 'No active subscriptions',
    subtitle: 'Browse the marketplace to subscribe to published strategies.',
    cta: { label: 'Browse marketplace', to: '/marketplace' },
  };
}

// ── Schedule table (no strategies vs filters hid everything) ───────────

export interface ScheduleTableEmptyContext {
  /** Total strategies on this day BEFORE client-side filters. When 0,
   *  the day genuinely has no strategies. When > 0, filters hid them. */
  totalStrategyCount: number;
  /** Available when the user has filters active so the caller can wire
   *  a "Clear filters" CTA. */
  onClearFilters?: () => void;
  isToday: boolean;
}

/**
 * Distinguishes "no strategies for this day at all" (route to baskets)
 * from "filters hide everything we have" (offer Clear filters as the
 * secondary CTA so the user doesn't have to hunt the filter row).
 */
export function pickScheduleTableEmpty(ctx: ScheduleTableEmptyContext): EmptyStatePreset {
  if (ctx.totalStrategyCount === 0) {
    return pickScheduleEmpty({ isToday: ctx.isToday, isPast: !ctx.isToday });
  }
  // Filters hide everything — keep the message focused on the action.
  return {
    title: 'No strategies match your filters',
    subtitle: 'Adjust the column filters or clear them to see all scheduled strategies.',
    cta: ctx.onClearFilters
      ? { label: 'Clear filters', onClick: ctx.onClearFilters }
      : undefined,
  };
}

// ── Positions table (Schedule's Positions tab — flat list empty) ───────

export function pickPositionsTableEmpty(ctx: { isToday: boolean }): EmptyStatePreset {
  if (ctx.isToday) {
    return {
      title: 'No positions yet today',
      subtitle: 'Positions will appear here once your strategies fire entry orders.',
      cta: { label: 'Manage baskets', to: '/baskets' },
    };
  }
  return {
    title: 'No positions on this day',
    subtitle: 'No positions were active on the selected historical date.',
  };
}
